import { singleton } from 'aurelia';

// Centralizes fetch access and the antiforgery (CSRF) handshake so every state-changing
// request goes through the same rotate-and-replay logic. Registered as a singleton and
// consumed by the account, subscription, saved-search, admin, and calendar services.
@singleton()
export class HttpClient {
  // Reads the JS-accessible XSRF-TOKEN cookie set by the server on GET requests and
  // returns it as the header the antiforgery service expects on state-changing POSTs.
  private csrfHeaders(): Record<string, string> {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
    return match ? { 'X-CSRF-TOKEN': decodeURIComponent(match[1]) } : {};
  }

  // A GET triggers the server middleware to re-issue a fresh XSRF-TOKEN cookie (bound
  // to the current identity), used to recover from a rotated/expired token.
  private async refreshCsrfToken(): Promise<void> {
    try {
      await fetch('/api/auth/me');
    } catch {
      // Non-fatal: the replay will use whatever token is currently present.
    }
  }

  // Plain GET passthrough so callers do not import fetch directly.
  get(url: string): Promise<Response> {
    return fetch(url);
  }

  // POSTs with the current CSRF header. Antiforgery rejects a rotated/expired token
  // with 400, so on a 400 we refresh the token via a GET and replay the request once.
  postWithCsrf(url: string, init: RequestInit = {}): Promise<Response> {
    return this.sendWithCsrf('POST', url, init);
  }

  // Like postWithCsrf but for any state-changing verb (POST/PUT/DELETE). Refreshes the
  // antiforgery token and replays once on a 400 (rotated/expired token).
  async sendWithCsrf(method: string, url: string, init: RequestInit = {}): Promise<Response> {
    const build = (): RequestInit => ({
      ...init,
      method,
      headers: { ...(init.headers as Record<string, string>), ...this.csrfHeaders() }
    });

    let response = await fetch(url, build());
    if (response.status === 400) {
      await this.refreshCsrfToken();
      response = await fetch(url, build());
    }
    return response;
  }
}
